import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import order from "lodash/orderBy";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import { useDeskCollectionInvoices } from "../../../api/hooks/deskCollection/deskCollection/useDeskCollectionInvoices.ts";
import { useDeskCollections } from "../../../api/hooks/deskCollection/deskCollection/useDeskCollections.ts";
import {
  CustomerType,
  DeskCollectionTypeFilterOptions,
  PaymentStatusFilterOptions,
} from "../../../graphql/generated.ts";
import {
  DeskCollection,
  DeskCollectionInvoice,
} from "../../../types/deskCollection/deskCollection.ts";
import { TAKE } from "../../../utils/pagination.ts";

interface Props {
  filter: {
    highestDpd: number | "";
    lowestDpd: number | "";
  };
  orderBy: DataTableSortStatus;
  page: number;
}

export function useDeskCollectionAutogroupData({
  filter,
  orderBy,
  page,
}: Props) {
  const [
    {
      data: invoicesResponse,
      error: invoicesError,
      fetching: fetchingInvoices,
    },
    refresh,
  ] = useDeskCollectionInvoices({
    filter: {
      paymentStatus: {
        notIn: [
          PaymentStatusFilterOptions.Paid,
          PaymentStatusFilterOptions.PaidWithLateFee,
        ],
      },
      dpd: {
        lte: filter.highestDpd === "" ? undefined : filter.highestDpd,
        gte: filter.lowestDpd === "" ? undefined : filter.lowestDpd,
      },
    },
    pause: filter.highestDpd === "" || filter.lowestDpd === "",
  });
  const invoices = invoicesResponse?.invoices.data;
  const groupedInvoices = groupBy(invoices, "customer.id");

  const [
    {
      data: deskCollectionResponse,
      error: deskCollectionError,
      fetching: fetchingDeskCollection,
    },
    refreshDeskCollections,
  ] = useDeskCollections({
    filter: {
      invoices: { some: { id: { in: map(invoices, "id") } } },
      type: { equals: DeskCollectionTypeFilterOptions.Automatic },
    },
  });
  const deskCollections = deskCollectionResponse?.deskCollections.data;

  const orderByAccessor = useMemo(() => {
    const { columnAccessor } = orderBy;
    switch (columnAccessor) {
      case "customerName":
        return ["customer.name"];
      default:
        return [];
    }
  }, [orderBy]);

  const mapDataRow = useCallback(
    (invoices: DeskCollectionInvoice[]) => {
      const customer = invoices[0].customer;
      const pic = customer.pic?.find((p) => p.type === CustomerType.Contact);
      const deskCollection = deskCollections?.find((dc) => {
        return isEqual(map(dc.invoices, "id"), map(invoices, "id"));
      });
      return {
        id: 0,
        picName: pic?.name,
        phone: pic?.phone,
        customer,
        invoices,
        template: null,
        response: null,
        logs: [],
        ...deskCollection,
      };
    },
    [deskCollections]
  );

  const data: DeskCollection[] = useMemo(() => {
    const result = Object.keys(groupedInvoices).map((customerId) => {
      const invoices = groupedInvoices[customerId];
      return mapDataRow(invoices);
    });
    return order(result, orderByAccessor, [orderBy.direction]);
  }, [groupedInvoices, mapDataRow, orderBy.direction, orderByAccessor]);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * TAKE;
    const end = start + TAKE;
    return data.slice(start, end);
  }, [data, page]);

  const total = Object.keys(groupedInvoices).length;

  const handleRefresh = useCallback(() => {
    refresh();
    refreshDeskCollections();
  }, [refresh, refreshDeskCollections]);

  return {
    error: invoicesError || deskCollectionError,
    fetching: fetchingInvoices || fetchingDeskCollection,
    refresh: handleRefresh,
    total,
    data: paginatedData,
  };
}
